import { PartialRecursive } from '@bridebook/models/source/abstract/_';
import type { ISeoScoreBoostSettings } from '@bridebook/models/source/models/Countries/Buckets.types';
import { ISupplierDetailVenue } from '@bridebook/models/source/models/Suppliers.types';
import gazetteer from '../../gazetteer';
import { resolveSupplierBucketSetting } from '../../gazetteer/resolve-supplier-bucket-setting';
import { ISupplierDataAll, SupplierTier, VenueTier } from '../suppliers/types';

/**
 * Maintain seo score to sort suppliers in logged-out and decide on index/no-index.
 * https://bridebook.atlassian.net/browse/LIVE-16711
 *
 * Contains Short Description: 0.1
 * Contains Long Description: 0.1
 * Contains Long Description above 500 characters: 0.1
 * Contains Long Description above 1000 characters: 0.5
 * Contains 5 images or more: 0.1
 * Contains 1 or more videos: 0.1
 * Contains venue features: 0.05
 * Contains Food and Drink features: 0.05
 * Contains Ceremony and Reception features: 0.05
 * Contains FAQs: 0.1
 * Contains 3 or more FAQs: 0.05
 * Contains Styles: 0.1
 * Contains reviews: 0.05
 * Contains 5 or more reviews: 0.1
 * Contains Social media links: 0.05
 * If a profile is currently paying a bridebook subscription, boost SEOScore by:
 *  - Registered Venue Tier 0 and -1 (Novice and Unverified): 0.3
 *  - Venue Tier 1 (Basic): 1.0
 *  - Venue Tier 2, 2.5 (Classic): 2.0
 *  - Venue Tier 3 (Expert): 3.0
 *  - Venue Tier 4 (Platinum): 3.5
 *  - Supplier Tier 1 (Basic): 0.5
 *  - Supplier Tier 2 (Classic): 0.6
 *  Non-venue paid suppliers get an extra per-tier boost
 */

const seoWeights = {
  shortDescriptionExists: 0.1,
  longDescriptionExists: 0.1,
  longDescription500: 0.1,
  longDescription1000: 0.5,
  photos: 0.1,
  videos: 0.1,
  venueFeatures: 0.05,
  foodAndDrinksFeatures: 0.05,
  ceremonyFeatures: 0.05,
  faq: 0.1,
  faq3: 0.05,
  styles: 0.1,
  reviews: 0.05,
  reviews5: 0.1,
  socialLinks: 0.05,
  venueTier0AndLowerRegistered: 0.3,
  venueTier1: 1.0,
  venueTier2: 2.0,
  venueTier3: 3.0,
  venueTier4: 3.5,
  topSpotVenue: 4.0,
  supplierTier1: 0.5,
  supplierTier2: 0.6,
};

export const SEO_SCORE_NOINDEX_THRESHOLD = 0.6;

/**
 * LIVE-21010 Set noindex to profiles with SEO score below the threshold
 *
 * @param score - The SEO score of the supplier profile.
 *
 * @returns Whether the profile should be indexed (true) or not (false).
 */
export const isSeoScoreIndexable = (score: number): boolean => score >= SEO_SCORE_NOINDEX_THRESHOLD;

export const getSeoScore = (supplierData: PartialRecursive<ISupplierDataAll>): number => {
  const seoScores: number[] = [];
  calculateDescriptionScore(supplierData, seoScores);
  calculateMediaScore(supplierData, seoScores);
  calculateFeaturesScore(supplierData, seoScores);
  calculateFaqScore(supplierData, seoScores);
  calculateReviewScore(supplierData, seoScores);
  calculateSocialScore(supplierData, seoScores);
  calculateTierScore(supplierData, seoScores);
  const sum = seoScores.reduce((prev, next) => prev + next, 0);
  return Number(sum.toFixed(2));
};

function calculateMediaScore(
  supplierData: PartialRecursive<ISupplierDataAll>,
  seoScores: number[],
) {
  if (supplierData?.photos && supplierData.photos.length >= 5) {
    seoScores.push(seoWeights.photos);
  }
  if (supplierData?.videos && supplierData.videos.length >= 1) {
    seoScores.push(seoWeights.videos);
  }
}

const calculateFeaturesScore = (
  supplierData: PartialRecursive<ISupplierDataAll>,
  seoScores: number[],
) => {
  if (!supplierData?.supplier) {
    return;
  }
  if (hasVenueFeatures(supplierData.supplier)) {
    seoScores.push(seoWeights.venueFeatures);
  }
  if (hasFoodAndDrinkFeatures(supplierData.supplier)) {
    seoScores.push(seoWeights.foodAndDrinksFeatures);
  }
  if (hasCeremonyFeatures(supplierData.supplier)) {
    seoScores.push(seoWeights.ceremonyFeatures);
  }
  if (hasStyles(supplierData.supplier)) {
    seoScores.push(seoWeights.styles);
  }
};

const calculateFaqScore = (
  supplierData: PartialRecursive<ISupplierDataAll>,
  seoScores: number[],
) => {
  if (supplierData.questions?.length) {
    seoScores.push(seoWeights.faq);
    if (supplierData.questions.length >= 3) {
      seoScores.push(seoWeights.faq3);
    }
  }
};

const calculateReviewScore = (
  supplierData: PartialRecursive<ISupplierDataAll>,
  seoScores: number[],
) => {
  if (supplierData.supplier?.counters?.reviews) {
    seoScores.push(seoWeights.reviews);
    if (supplierData.supplier.counters.reviews >= 5) {
      seoScores.push(seoWeights.reviews5);
    }
  }
};

const calculateSocialScore = (
  supplierData: PartialRecursive<ISupplierDataAll>,
  seoScores: number[],
) => {
  if (hasSocialLinks(supplierData?.supplier)) {
    seoScores.push(seoWeights.socialLinks);
  }
};

const calculateDescriptionScore = (
  supplierData: PartialRecursive<ISupplierDataAll>,
  seoScores: number[],
) => {
  const shortDescription = supplierData.supplier?.description?.short;
  if (shortDescription) {
    seoScores.push(seoWeights.shortDescriptionExists);
  }
  const longDescription = supplierData.supplier?.description?.long;
  if (longDescription) {
    seoScores.push(seoWeights.longDescriptionExists);
    if (longDescription.length >= 1000) {
      seoScores.push(seoWeights.longDescription1000);
    } else if (longDescription.length >= 500) {
      seoScores.push(seoWeights.longDescription500);
    }
  }
};

const hasVenueFeatures = (supplier: PartialRecursive<ISupplierDataAll['supplier']>) => {
  const featuresKeys: ISupplierDetailVenue['details'] = [
    'ac',
    'ballroom',
    'bridalChangingFacilities',
    'cityView',
    'decorations',
    'disabledAccess',
    'exclusiveUse',
    'gradeListedBuilding',
    'inHouseWeddingCoordinator',
    'landscapedGardens',
    'marqueePermitted',
    'onsiteParking',
    'openFlamePermitted',
    'other',
    'outdoorSpace',
    'petFriendly',
    'spaOnsite',
    'transportation',
    'waterView',
    'wifi',
  ];
  const details = supplier?.typeDetails?.venue?.details;
  if (!details) {
    return false;
  }
  for (const featuresKey of featuresKeys) {
    const featured = details.includes(featuresKey);
    const isOther = featuresKey === 'other';
    if ((featured && !isOther) || (isOther && supplier?.typeDetails?.venue?.detailsOther)) {
      return true;
    }
  }
  return false;
};

const hasFoodAndDrinkFeatures = (supplier: PartialRecursive<ISupplierDataAll['supplier']>) => {
  const featuresKeys: ISupplierDetailVenue['foodDrink'] = [
    'alcoholLicense',
    'asianCatering',
    'bar',
    'byobPermitted',
    'corkageCharge',
    'externalCateringPermitted',
    'halalCatering',
    'inHouseCatering',
    'inHouseWineList',
    'kosherCatering',
    'other',
    'vegan',
  ];
  const details = supplier?.typeDetails?.venue?.foodDrink;
  if (!details) {
    return false;
  }
  for (const featuresKey of featuresKeys) {
    const featured = details.includes(featuresKey);
    const isOther = featuresKey === 'other';
    if ((featured && !isOther) || (isOther && supplier.typeDetails?.venue?.foodDrinkOther)) {
      return true;
    }
  }
  return false;
};

const hasStyles = (supplier: PartialRecursive<ISupplierDataAll['supplier']>): boolean => {
  const supplierType = supplier?.type?.[0];
  switch (supplierType) {
    case 'venue':
      return Boolean(supplier?.typeDetails?.venue?.style?.length);
    case 'photo':
      return Boolean(supplier?.typeDetails?.photo?.photographyStyle?.length);
    case 'cakes':
      return Boolean(supplier?.typeDetails?.cakes?.styleOptions?.length);
    case 'florist':
      return Boolean(supplier?.typeDetails?.florist?.styleOptions?.length);
    case 'menswear':
      return Boolean(supplier?.typeDetails?.menswear?.styleOptions?.length);
    case 'music':
      return Boolean(supplier?.typeDetails?.music?.musicStyle?.length);
    case 'stationery':
      return Boolean(supplier?.typeDetails?.stationery?.styleOptions?.length);
    case 'video':
      return Boolean(supplier?.typeDetails?.video?.styleOptions?.length);
    default:
      return false;
  }
};

const hasCeremonyFeatures = (supplier: PartialRecursive<ISupplierDataAll['supplier']>) => {
  const featuresKeys: ISupplierDetailVenue['ceremonyReception'] = [
    'churchWithinWalkingDistance',
    'confettiPermitted',
    'fireworksPermitted',
    'inHouseDancefloor',
    'outdoorCeremonyLicence',
    'soundSystemAvailable',
    'weddingLicence',
    'other',
    'stage',
    'childcare',
  ];
  const details = supplier?.typeDetails?.venue?.ceremonyReception;
  if (!details) {
    return false;
  }
  for (const featuresKey of featuresKeys) {
    const featured = details.includes(featuresKey);
    const isOther = featuresKey === 'other';
    if (
      (featured && !isOther) ||
      (isOther && supplier.typeDetails?.venue?.ceremonyReceptionOther)
    ) {
      return true;
    }
  }
  return false;
};

type SocialPlatforms = keyof ISupplierDataAll['supplier']['social'];

const hasSocialLinks = (supplier?: PartialRecursive<ISupplierDataAll['supplier']>) => {
  const socialPlatforms = [
    'blog',
    'facebook',
    'instagram',
    'linkedin',
    'pinterest',
    'twitter',
    'vimeo',
    'youtube',
  ] as SocialPlatforms[];
  for (const socialPlatform of socialPlatforms) {
    if (supplier?.social?.[socialPlatform]) {
      return true;
    }
  }
  return false;
};

const calculateTierScore = (
  supplierData: PartialRecursive<ISupplierDataAll>,
  seoScores: number[],
) => {
  const tier = supplierData.admin?.premium?.tier || 0;
  const isVenue = supplierData.supplier?.type?.[0] === 'venue';

  // LIVE-25387 Use tier ranges to cover newly introduced tiers.
  if (isVenue) {
    if (tier >= VenueTier.Tier_4) {
      seoScores.push(seoWeights.venueTier4);
    } else if (tier >= VenueTier.Tier_3) {
      seoScores.push(seoWeights.venueTier3);
    } else if (tier >= VenueTier.Tier_2) {
      seoScores.push(seoWeights.venueTier2);
    } else if (tier > VenueTier.Tier_0) {
      seoScores.push(seoWeights.venueTier1);
    } else if (
      Boolean(supplierData?.admin?.registrationDate) &&
      (tier === VenueTier.Tier_0 || tier === VenueTier.Unverified)
    ) {
      seoScores.push(seoWeights.venueTier0AndLowerRegistered);
    }
  } else {
    if (tier >= SupplierTier.Tier_2) {
      seoScores.push(seoWeights.supplierTier2);
    } else if (tier > SupplierTier.Tier_0) {
      seoScores.push(seoWeights.supplierTier1);
    }

    const type = supplierData.supplier?.type?.[0];
    const country = supplierData.supplier?.l10n?.country;
    const market = country ? gazetteer.getMarketByCountry(country) : undefined;
    const { isEnabled, setting } = resolveSupplierBucketSetting(
      'seoScoreBoost',
      type,
      market,
      tier,
    );
    if (isEnabled) {
      const tierBoost = setting.tiers?.[String(tier) as keyof ISeoScoreBoostSettings['tiers']];
      if (tierBoost) {
        seoScores.push(tierBoost);
      }
    }
  }
};
